import { ContentfulQueryKeys, useContentfulQuery } from '@oresundsbron/api';
import { useMemo } from 'react';
import { pipe } from 'fp-ts/lib/function';
import { fromNullable, map, toUndefined } from 'fp-ts/lib/Option';
import { useRouter } from 'next/router';
import { useSEOMetadata } from './useSEOMetaData';
import { createMetaDataItems } from '~/lib/metadata';
import { PageFilter } from '../types/page';
import { useLinksV2 } from './useLinksV2';

const useFAQ = () => {
  const { locale, isPreview } = useRouter();
  const { data } = useContentfulQuery(
    ContentfulQueryKeys.FAQ({ locale, preview: isPreview, main: true }),
    (q) => q.FAQ.get({ locale, preview: isPreview, main: true })
  );

  return useMemo(
    () =>
      pipe(
        data,
        fromNullable,
        map((d) => d.groups),
        toUndefined
      ),
    [data]
  );
};

export default useFAQ;

export const useFAQQuestion = (filter: PageFilter) => {
  const { locale, isPreview } = useRouter();
  const { getLink } = useLinksV2();
  const { data, error, isLoading } = useContentfulQuery(
    ContentfulQueryKeys.FAQQuestion({ ...filter, locale, preview: isPreview }),
    (q) => q.FAQ.getFaqQuestion(locale || '', filter.id, isPreview),
    { staleTime: Infinity, enabled: !!filter.id }
  );
  const link = getLink(filter.id);

  const metadataItems = createMetaDataItems({
    ...data,
    link,
    contentType: 'website',
  });

  const seoMetadata = useSEOMetadata(data?.metadata, metadataItems);

  return { data, error, seoMetadata, isLoading };
};
